import React, { useEffect, useLayoutEffect, useMemo } from "react";

import { Outlet, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setGlobalLoader } from "../../../redux/slices/globalLoader";
import { addData } from "../../../redux/slices/globalDetails";
import { GlobalSettingsDetails } from "../services/website.services";
import { clearUserSession, hideMainLoader, setLocalKey, isMissingPhoneNumber, isUserMobileMissing } from "../../../utils/commonfunction";
import Header from "../../user/components/Header";
import Footer from "../../user/components/Footer";
import Swal from "sweetalert2";
import { ROLE, SWAL_SETTINGS, SOCKET_EVENTS } from "../../../utils/Constants";
import { addProfileData } from "../../../redux/slices/profileslice";
import * as UserService from "../../user/services/user.service";
import { SOCKET_CLIENT } from "../../../utils/socket";
import { getUser } from "../../../utils/commonfunction";

function WebsiteLayout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  window.navigate_ = navigate;

  useEffect(() => {
    const isProfileEdit = pathname === "/user/profile-edit";
    const isAllowedAuthPath =
      pathname.startsWith("/user/login") ||
      pathname.startsWith("/user/signup") ||
      pathname.startsWith("/user/otp") ||
      pathname.startsWith("/user/forgot-password") ||
      pathname.startsWith("/user/reset-password") ||
      pathname.startsWith("/auth/") ||
      pathname.startsWith("/select-role");

    if (!isProfileEdit && !isAllowedAuthPath && isUserMobileMissing()) {
      Swal.fire({
        icon: "warning",
        title: "Mobile Number Required",
        text: "Please add your mobile number to continue.",
        confirmButtonColor: "var(--primary-400, #ff4081)",
        confirmButtonText: "Add Mobile Number",
      });
      navigate("/user/profile-edit", { replace: true });
    }
  }, [pathname]);

  // Re-trigger AdSense for SPA route navigation
  useEffect(() => {
    try {
      if (typeof window !== "undefined" && window.adsbygoogle) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {
      // Ignore SPA push warnings
    }
  }, [pathname]);

  useMemo(() => {
    GlobalSettingsDetails().then(async (globalDetails) => {
      dispatch(addData(JSON.stringify(globalDetails?.data)));
      setTimeout(() => {
        dispatch(setGlobalLoader(false));
      }, 100);
    });
  }, []);

  // --- Global Socket Notifications (Staff & Stream Reminders) ---
  useEffect(() => {
    const joinPersonalRoom = () => {
      const userData = getUser(ROLE.USER) || getUser();
      if (userData && (userData._id || userData.id)) {
        const uid = String(userData._id || userData.id);
        console.log("👤 [WebsiteLayout] Joining personal socket room:", uid);
        SOCKET_CLIENT.emit("join_personal_room", { userId: uid, token: userData.token });
      }
    };

    if (SOCKET_CLIENT.connected) {
      joinPersonalRoom();
    }

    const handleConnect = () => {
      console.log("✅ [Socket] Connected in WebsiteLayout. ID:", SOCKET_CLIENT.id);
      joinPersonalRoom();
    };

    SOCKET_CLIENT.on("connect", handleConnect);

    // 1. Staff Stream Started
    const handleStaffStreamStarted = (data) => {
      console.log("🔔 [Staff] Stream Started received:", data);
      window.dispatchEvent(new Event("NEW_NOTIFICATION_RECEIVED"));
      Swal.fire({
        title: data?.title || "Stream Live Now! 🔴",
        text: data?.message || "A stream you are assigned to is now live!",
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Manage Stream",
        cancelButtonText: "Later",
        confirmButtonColor: "#FF63B5",
        toast: true,
        position: "top-end",
        timer: 20000,
        timerProgressBar: true,
      }).then((result) => {
        if (result.isConfirmed && data?.streamId) {
          navigate(`/user/staff-stream/${data.streamId}`);
        }
      });
    };

    // 2. Staff Stream Assigned
    const handleStaffStreamAssigned = (data) => {
      console.log("🔔 [Staff] Stream Assigned received:", data);
      window.dispatchEvent(new Event("NEW_NOTIFICATION_RECEIVED"));
      Swal.fire({
        title: data?.title || "New Stream Assignment 📅",
        text: data?.message || "You have been assigned to an upcoming stream!",
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "View Streams",
        cancelButtonText: "Close",
        confirmButtonColor: "#FF63B5",
        toast: true,
        position: "top-end",
        timer: 15000,
        timerProgressBar: true,
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/user/assigned-streams");
        }
      });
    };

    // 3. Staff 10-Minute Reminder
    const handleStaffStreamReminder = (data) => {
      console.log("🔔 [Staff] 10-Min Stream Reminder received:", data);
      window.dispatchEvent(new Event("NEW_NOTIFICATION_RECEIVED"));
      Swal.fire({
        title: data?.title || "Stream Starting in 10 Minutes! ⏰",
        text: data?.message || "Upcoming stream starts in 10 minutes. Please get ready!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Open Stream",
        cancelButtonText: "Dismiss",
        confirmButtonColor: "#FF63B5",
        toast: true,
        position: "top-end",
        timer: 20000,
        timerProgressBar: true,
      }).then((result) => {
        if (result.isConfirmed && data?.streamId) {
          navigate(`/user/staff-stream/${data.streamId}`);
        }
      });
    };

    // 4. Viewer Stream Started Reminder
    const handleViewerReminder = (data) => {
      console.log("🔔 [Viewer] Stream Reminder received:", data);
      window.dispatchEvent(new Event("NEW_NOTIFICATION_RECEIVED"));
      const currentUser = getUser(ROLE.USER);
      if (currentUser && currentUser.stream_notification === false) {
        return;
      }

      Swal.fire({
        title: data?.title || "Stream Starting!",
        text: data?.message || "A stream you follow is starting now!",
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Watch Now",
        cancelButtonText: "Later",
        confirmButtonColor: "#FF63B5",
        toast: true,
        position: "top-end",
        timer: 15000,
        timerProgressBar: true,
      }).then((result) => {
        if (result.isConfirmed && data?.streamId) {
          navigate(`/user/live-streams/${data.publisherID}/${data.roomID}/${data.streamId}`);
        }
      });
    };

    SOCKET_CLIENT.on(SOCKET_EVENTS.STAFF_STREAM_STARTED, handleStaffStreamStarted);
    SOCKET_CLIENT.on(SOCKET_EVENTS.STAFF_STREAM_ASSIGNED, handleStaffStreamAssigned);
    SOCKET_CLIENT.on(SOCKET_EVENTS.STAFF_STREAM_REMINDER, handleStaffStreamReminder);
    SOCKET_CLIENT.on(SOCKET_EVENTS.STREAM_STARTED_REMINDER, handleViewerReminder);

    return () => {
      SOCKET_CLIENT.off("connect", handleConnect);
      SOCKET_CLIENT.off(SOCKET_EVENTS.STAFF_STREAM_STARTED, handleStaffStreamStarted);
      SOCKET_CLIENT.off(SOCKET_EVENTS.STAFF_STREAM_ASSIGNED, handleStaffStreamAssigned);
      SOCKET_CLIENT.off(SOCKET_EVENTS.STAFF_STREAM_REMINDER, handleStaffStreamReminder);
      SOCKET_CLIENT.off(SOCKET_EVENTS.STREAM_STARTED_REMINDER, handleViewerReminder);
    };
  }, [navigate]);

  useLayoutEffect(() => {
    // --- Auth Token Detection from URL (e.g. Social / Apple login redirect to root "/") ---
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const error = params.get("error");

    // Only process authentication login tokens if on root path and token is a valid JWT format (3 parts separated by dots)
    const isRootOrAuthPath = pathname === "/" || pathname === "/auth/apple-callback";
    const isJwtToken = typeof token === "string" && token.split(".").length === 3;

    if (token && isRootOrAuthPath && isJwtToken) {
      try {
        // 1. Decode token to get user _id
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        const payload = JSON.parse(jsonPayload);
        const userId = payload?._id;

        if (userId) {
          // 2. Set the temporary token and _id to allow Header to fetch profile
          clearUserSession();
          const tempUser = { token: token, _id: userId, role: ROLE.USER };
          setLocalKey(ROLE.USER, JSON.stringify(tempUser));
          dispatch(addProfileData(tempUser)); // Trigger Redux update for Header immediately

          // 3. Clear token from URL immediately (clean address bar)
          setSearchParams({}, { replace: true });

          // 4. Fetch full profile and update Redux
          UserService.profileDetails({ _id: userId || "me" })
            .then((response) => {
              if (response.success) {
                const fullUserData = { ...response.data, token: token };
                setLocalKey(ROLE.USER, JSON.stringify(fullUserData));
                dispatch(addProfileData(fullUserData));

                if (isMissingPhoneNumber(fullUserData.phone_number)) {
                  Swal.fire({
                    icon: "warning",
                    title: "Mobile Number Required",
                    text: "Please add your mobile number to continue.",
                    confirmButtonColor: "var(--primary-400, #ff4081)",
                    confirmButtonText: "Add Mobile Number",
                  }).then(() => {
                    navigate("/user/profile-edit");
                  });
                  return;
                }

                Swal.fire({
                  icon: "success",
                  text: "Login successful",
                  ...SWAL_SETTINGS,
                });
              } else {
                Swal.fire({
                  icon: "warning",
                  text: response.message || "LoggedIn but failed to fetch profile details",
                  ...SWAL_SETTINGS,
                });
              }
            })
            .catch((err) => {
              console.error("Profile fetch error:", err);
            });
        }
      } catch (err) {
        console.error("Token decoding error:", err);
      }
    } else if (error && isRootOrAuthPath) {
      setSearchParams({}, { replace: true });
      Swal.fire({
        icon: "error",
        text: decodeURIComponent(error),
        ...SWAL_SETTINGS,
      });
    }
    // ------------------------------------

    Promise.all([
      // import("../../../assets/website/css/style.css"),
      // import("../../../assets/website/css/responsive.css"),
    ]).then(() => {
      setTimeout(() => {
        hideMainLoader();
      }, 100);
    });
    const body = document.querySelector("body, html");
    if (body) body.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [dispatch, setSearchParams, pathname]);

  return (
    <div className="site-bg">
      <Header />
      <div className="inner-wraper pt-[94px]">
        <Outlet />
      </div>
      <Footer />
    </div>
  );
}

export default WebsiteLayout;
